import React from 'react';
import { Routes, Navigate, Route, BrowserRouter } from 'react-router-dom';
import AuthGuard from '@/guards/Auth.tsx';

// Main
import WeekPage from '@/pages/Week';
import DayPage from '@/pages/Day';
// Auth
import Login from '@/pages/Auth/Login';
import Signup from '@/pages/Auth/Signup';
import ChangePassword from '@/pages/Auth/ChangePassword';
import ForgotPassword from '@/pages/Auth/ForgotPassword';
import ResendVerify from '@/pages/Auth/ResendVerify';
import CheckVerify from '@/pages/Auth/CheckVerify';
import Verify from '@/pages/Auth/Verify';
// Settings
import AccountDetails from '@/pages/Settings/AccountDetails';
import Security from '@/pages/Settings/Security';
import AppSettings from '@/pages/Settings/AppSettings';
import Projects from '@/pages/Settings/Projects';
// Other
import PageNotFound from '@/pages/404';
import { useAppDispatch } from '@/store/hooks.tsx';
import { initAuth } from '@/store/user/thunkActions.ts';
import AuthLayout from '@/layouts/Auth';
import MainLayout from '@/layouts/Main';
import SettingsLayout from '@/layouts/Settings';

interface PathConfig {
  path: string;
  element: React.ReactNode;
  layout?: React.FC<{ children: React.ReactNode }> | undefined;
  isProtected?: boolean;
}

const getDefaultRedirect = () => {
  if (window.innerWidth < 576) {
    return `/day/${new Date().getTime()}`;
  } else {
    return `/week/${new Date().getTime()}`;
  }
};

const pathConfigRoutes: PathConfig[] = [
  {
    path: '/',
    element: <Navigate replace to={getDefaultRedirect()} />,
    isProtected: true,
  },
  {
    path: '/login',
    element: <Login />,
    layout: AuthLayout,
  },
  {
    path: '/signup',
    element: <Signup />,
    layout: AuthLayout,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
    layout: AuthLayout,
  },
  {
    path: '/change-password',
    element: <ChangePassword />,
    layout: AuthLayout,
  },
  {
    path: '/check-verify',
    element: <CheckVerify />,
    layout: AuthLayout,
  },
  {
    path: '/resend-verify',
    element: <ResendVerify />,
    layout: AuthLayout,
    isProtected: false,
  },
  {
    path: '/verify',
    element: <Verify />,
    layout: AuthLayout,
  },
  {
    path: '/week/:dayInMs',
    element: <WeekPage/>,
    layout: MainLayout,
    isProtected: true,
  },
  {
    path: '/day/:dayInMs',
    element: <DayPage/>,
    layout: MainLayout,
    isProtected: true,
  },
  {
    path: '/settings/account-details',
    element: <AccountDetails />,
    layout: SettingsLayout,
    isProtected: true,
  },
  {
    path: '/settings/security',
    element: <Security />,
    layout: SettingsLayout,
    isProtected: true,
  },
  {
    path: '/settings/app-settings',
    element: <AppSettings />,
    layout: SettingsLayout,
    isProtected: true,
  },
  {
    path: '/settings/projects',
    element: <Projects />,
    layout: SettingsLayout,
    isProtected: true,
  },
  {
    path: '*',
    element: <PageNotFound />,
  }
];

const AppRoutes = () => {
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    dispatch(initAuth());
  }, [dispatch]);

  return (
    <BrowserRouter>
      <AuthGuard>
        <Routes>
          {pathConfigRoutes.map(({ path, element, layout: Layout }, index) => {
            const WrappedElement = Layout ? (
              <Layout>
                {element}
              </Layout>
            ) : (
              element
            );

            return (
              <Route
                key={index + "Route"}
                path={path}
                element={WrappedElement}
              />
            );
          })}
        </Routes>
      </AuthGuard>
    </BrowserRouter>
  );
};

export default AppRoutes;
